import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useLocation, useParams, useHistory } from 'react-router-dom'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Col,
  Row,
  FormGroup,
  Form,
  Label,
  Button,
  Modal,
  Table,
} from 'react-bootstrap'
import AccordionChildren from './accordion/AccordionChildren'
import ResourceMenu from './ResourceMenu'
import { PrintArea } from './PrintArea'
import PdfOverlayDesigner from './PdfOverlayDesigner'
import { isPdfOverlayOutputMode } from './reportOutputMode.util'
import {
  clearPdfOverlayGeometry,
  hasIncompatiblePdfOverlayGeometry,
  isPdfTemplateMetadataPending,
  isPdfTemplateRevisionChanged,
} from './pdfOverlayRevision.util'
import './CreateReport.scss'
import PropertiesMenu from './PropertiesMenu'
import LayoutMenu from './LayoutMenu'
import PublishMenu from './PublishMenu'
import DataSourceMenu from './DataSourceMenu'
import Breadcrumb from './breadcrumb/Breadcrumb'
import { HeaderArea } from './HeaderArea'
import httpClient from 'utility/httpClient.util'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import Select, { components } from 'react-select'
import Icon from './Icon'
import _ from 'lodash'
import { invalidateChangedPdfGeometry } from './pdfOverlayMeasurement.util'
import { Pagination, PaginationItem } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import ParameterMenu from './ParameterMenu'
import ExportImportMenu from './ExportImportMenu'
import {
  buildPdfResourceItem,
  createPdfTemplateResourceItem,
  getAttachmentId,
  uploadReportStaticFile,
  uploadReportImageResource,
} from './resourceUpload'
import {
  createReportDataSourceMap,
  getReportBindValue,
} from './reportBind.util'
import {
  buildPortableReportExportData,
  uploadPortableReportAssets,
} from './reportPortableImage.util'
import {
  getReportResourceCode,
  getReportResourceCodes,
  getReportResourceItems,
  isReportResourceOutput,
  isReportItemVisibleOnLayoutPage,
  normalizeReportResourcePublishData,
} from './reportResource.util'
import useAuth from 'hooks/useAuth'
import {
  clearReportDraft,
  getReportDraft,
  getReportDraftFormData,
  saveReportDraft,
} from './ReportDraftCache'
import CodexAgentFloatingMenu from '../Report/CodexAgentFloatingMenu'
import ExcelDataPreview from './ExcelDataPreview'

const useStyles = makeStyles((theme) => ({
  root: {
    '& > *': {
      justifyContent: 'right',
      display: 'flex',
    },
  },
  paginate: {
    '& > *': {
      padding: '10px',
      margin: '0px 15px',
      backgroundColor: 'white',
      borderRadius: '5px',
    },
    '& .MuiPaginationItem-root.Mui-selected': {
      color: '#7367f0',
      border: '1px solid #7367f0',
    },
  },
}))

const REVISION_PAGE_SIZE = 10

const DEFAULT_BOX = {
  top: 0,
  left: 0,
  title: '',
  itemType: 'text',
  fieldName: '',
  defaultValue: '',
  value: '',
  type: 'text',
}

const PROPERTIES_INDEX = 5
const PARAMETER_INDEX = 4
const MAX_HISTORY = 50
const cloneReportData = (value) =>
  typeof structuredClone === 'function'
    ? structuredClone(value)
    : _.cloneDeep(value)

const REPORT_ALIAS_CODE_PATTERN = /^[A-Za-z0-9_-]{2,50}$/
const normalizeAliasCode = (value) => `${value || ''}`.trim()
const normalizeReportTemplateCode = (value) => {
  const code = normalizeAliasCode(value)
  return code || null
}
const normalizeReportAliases = (aliases = []) =>
  _.uniqBy(
    (aliases || [])
      .map((item) => ({
        reportTemplateAliasId: item?.reportTemplateAliasId,
        code: normalizeAliasCode(item?.code),
        enabled: item?.enabled !== false,
      }))
      .filter((item) => item.code),
    'code',
  )
const getReportAliasPayload = (aliases = [], reportTemplateId = null) =>
  normalizeReportAliases(aliases).map(
    ({ reportTemplateAliasId, code, enabled }) => ({
      ...(reportTemplateAliasId ? { reportTemplateAliasId } : {}),
      ...(reportTemplateId ? { reportTemplateId: Number(reportTemplateId) } : {}),
      code,
      enabled: enabled !== false,
    }),
  )

const normalizeReportCategoryIds = (categoryIds = []) =>
  _.uniq(
    (categoryIds || [])
      .map((categoryId) => Number(categoryId))
      .filter((categoryId) => Number.isFinite(categoryId) && categoryId > 0),
  )

const getReportCategoryIdFromItem = (item = {}) =>
  item?.category?.categoryId || item?.categoryId

const getReportTemplateCategoryList = (obj = {}) =>
  obj?.reportTemplate?.reportTemplateCategories ||
  obj?.reportTemplateCategories ||
  []

const getReportTemplateCategoryIds = (obj = {}) =>
  normalizeReportCategoryIds(
    getReportTemplateCategoryList(obj).map(getReportCategoryIdFromItem),
  )

const getPrimaryReportCategoryName = (obj = {}) => {
  const item = getReportTemplateCategoryList(obj).find(
    (categoryItem) => categoryItem?.category?.name || categoryItem?.category?.code,
  )

  return item?.category?.name || item?.category?.code || ''
}

const getReportTagCollectionList = (obj = {}) =>
  obj?.reportTemplate?.reportTagCollections || obj?.reportTagCollections || []

const hasReportTagCollectionData = (obj = {}) =>
  Object.prototype.hasOwnProperty.call(obj || {}, 'reportTagCollections') ||
  Object.prototype.hasOwnProperty.call(obj?.reportTemplate || {}, 'reportTagCollections')

const normalizeReportTagIds = (tagIds = []) =>
  _.uniq(
    (tagIds || [])
      .map((tagId) => Number(tagId))
      .filter((tagId) => Number.isFinite(tagId) && tagId > 0),
  )

const getReportTagIdFromItem = (item = {}) =>
  item?.reportTag?.reportTagId || item?.tag?.reportTagId || item?.reportTagId

const getReportTemplateTagIds = (obj = {}) =>
  normalizeReportTagIds(getReportTagCollectionList(obj).map(getReportTagIdFromItem))

const mapReportTagOption = (tag = {}) => ({
  label: tag?.label || tag?.title || '',
  value: tag?.value || tag?.reportTagId,
  reportTagId: tag?.reportTagId || tag?.value,
  title: tag?.title || tag?.label || '',
  description: tag?.description || '',
  hexColor: tag?.hexColor || '',
})

const mergeReportTagOptions = (options = [], newOptions = []) =>
  _.uniqBy(
    [...(options || []), ...(newOptions || [])]
      .map(mapReportTagOption)
      .filter((tag) => tag.reportTagId || tag.title),
    (tag) => tag.reportTagId || tag.title,
  )

const getReportTags = (obj = {}) =>
  _.uniqBy(
    getReportTagCollectionList(obj)
      .map((item) => item?.reportTag || item?.tag || item)
      .filter((tag) => tag?.reportTagId || tag?.title)
      .map(mapReportTagOption),
    (tag) => tag?.reportTagId || tag?.title,
  )

const normalizeHexColor = (value) => {
  const color = String(value || '').trim()
  return /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(color)
    ? color
    : ''
}

const getContrastTextColor = (hexColor) => {
  const color = normalizeHexColor(hexColor).replace('#', '')
  if (!color) return '#31546f'
  const rgb = color.length === 3
    ? color.split('').map((item) => parseInt(item + item, 16))
    : [color.slice(0, 2), color.slice(2, 4), color.slice(4, 6)].map((item) => parseInt(item, 16))
  const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255

  return luminance > 0.6 ? '#17202a' : '#fff'
}

export const languageOptionWithFlag = [
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'th', label: 'ไทย', flag: '🇹🇭' },
]

const SAMPLE_DATA = {
  a: Object.assign({}, DEFAULT_BOX, {
    id: 'a',
    top: 20,
    left: 80,
    value: 'Drag me around',
    type: 'date',
  }),
  b: Object.assign({}, DEFAULT_BOX, {
    id: 'b',
    top: 180,
    left: 20,
    value: 'Drag me too',
    type: 'image',
    width: 128,
    height: 128,
  }),
}

const CLASSIC_REPORT_CODEX_SYSTEM_PROMPT = `
You are helping update a CK Doctor Classic Report.
Read backend docs/specs/report/README.CLASSIC_REPORT.md and docs/schema/ckdoctor.schema.json before proposing SQL or report JSON.
Return only one JSON artifact. Do not include markdown outside the JSON.
Use this contract:
{
  "action": "replace_classic_report",
  "reportTemplateId": number,
  "dataSourceData": { "list": [] },
  "printData": {},
  "layoutData": {},
  "publishData": {},
  "paramsData": { "inputs": [] },
  "notes": []
}
Preserve existing report metadata unless the user asks to change it.
Use parameter placeholders like "{{hn}}", "{{vn}}", "{{an}}", or "{{documentNo}}".
Use double-quoted PostgreSQL identifiers and SQL aliases that match token names.
`.trim()

const { Option } = components
const IconOption = (props) => (
  <Option {...props}>
    {props.data.iconName && <Icon name={props.data.iconName} />}
    <span style={{ marginLeft: '5px' }}>{props.data.label}</span>
  </Option>
)

const CustomSingleValue = ({ data }) => (
  <div className="input-select">
    <div className="input-select__single-value">
      <Icon name={data.iconName} />
      <span style={{ marginLeft: '5px' }}>{data.label}</span>
    </div>
  </div>
)

const CreateReport = ({ reportTemplateId = null, showBreadcrumb = true }) => {
  const { id, revisionId } = useParams()

  const routeHistory = useHistory()
  const classes = useStyles()
  const { user } = useAuth()
  const [syReportId, setSyReportId] = useState(0)
  const [maxId, setMaxId] = useState(0)
  const [openId, setOpenId] = useState(2)
  const [activeItem, setActiveItem] = useState({})
  const [selectedItemIds, setSelectedItemIds] = useState([])
  const [areaSelectionBox, setAreaSelectionBox] = useState(null)
  const [reportName, setReportName] = useState('')
  const [reportCode, setReportCode] = useState('')
  const [reportAliases, setReportAliases] = useState([])
  const [reportTags, setReportTags] = useState([])
  const [showAliasModal, setShowAliasModal] = useState(false)
  const [aliasModalIndex, setAliasModalIndex] = useState(null)
  const [aliasModalCode, setAliasModalCode] = useState('')
  const [moduleName, setModuleName] = useState('')
  const [syReport, setSyReport] = useState({
    publishData: { moduleName: '', locationCode: '', langReportCode: {},reportTenantCode:'',reportUuid:'' },
    reportCode: '',
  })
  const [categoryList, setCategoryList] = useState([])
  const [reportTagList, setReportTagList] = useState([])
  const [tenantList, setTenantList] = useState([])
  const [languageOption, setLanguageOption] = useState(languageOptionWithFlag)

  const [dataSourceList, setDataSourceList] = useState([])
  const [designerViewMode, setDesignerViewMode] = useState('report')
  const [inputs, setInputs] = useState([])
  const [layout, setLayout] = useState({
    size: 'A4',
    orientation: 'portrait',
    width: '21cm',
    height: '29.7cm',
    paddingLeft: '0.5in',
    paddingRight: '0.5in',
    paddingTop: '0.5in',
    paddingBottom: '0.5in',
    pages: 1,
  })
  const [boxes, setBoxes] = useState({})
  const [locationList, setLocationList] = useState([])

  const [currPage, setCurrPage] = useState(1)
  const [permissionsDefault, setPermissionsDefault] = useState([])

  const [showModalPermission, setShowModalPermission] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isReportDataLoaded, setIsReportDataLoaded] = useState(false)
  const [reportDraft, setReportDraft] = useState(null)
  const [isReportDraftLoaded, setIsReportDraftLoaded] = useState(false)
  const [reportDraftStatus, setReportDraftStatus] = useState('')
  const [showRevisionModal, setShowRevisionModal] = useState(false)
  const [revisionList, setRevisionList] = useState([])
  const [revisionPagination, setRevisionPagination] = useState({
    page: 1,
    perPage: REVISION_PAGE_SIZE,
    total: 0,
    lastPage: 1,
  })
  const [isRevisionLoading, setIsRevisionLoading] = useState(false)
  const [activeRevision, setActiveRevision] = useState(null)
  const isRevisionMode = Boolean(activeRevision?.revisionId)

  const activeItemRef = useRef(activeItem)
  const reportAreaRef = useRef(null)
  const areaSelectionBoxRef = useRef(null)
  const suppressReportAreaClickRef = useRef(false)
  const reportDraftBaseSnapshotRef = useRef('')
  const reportDraftTimerRef = useRef(null)
  const reportDraftOwnerRef = useRef(null)
  const [reportHistory, setReportHistory] = useState({
    past: [],
    present: {},
    future: [],
  })

  useEffect(() => {
    activeItemRef.current = activeItem
  }, [activeItem])

  const handleShowPermission = () => {
    setShowModalPermission(true)
  }

  const handleClosePermission = () => {
    setShowModalPermission(false)
  }

  const handleChangePage = (event, value) => {
    setCurrPage(value)
  }

  const savePermission = async (item) => {
    setPermissionsDefault(item)
    setShowModalPermission(false)
  }

  const clearActiveSelection = () => {
    setActiveItem({})
    setSelectedItemIds([])
  }

  const useSearch = useLocation().search

  const getLoadedReportAliases = (obj) =>
    normalizeReportAliases(
      obj?.reportTemplate?.reportTemplateAliases ||
        obj?.reportTemplateAliases ||
        obj?.reportTemplate?.reporttemplatealias ||
        obj?.reporttemplatealias ||
        [],
    )

  const loadReportTemplateData = async (reportId) => {
    if (!reportId) return null

    try {
      return await httpClient
        .get(
          '/report/report-templates/' +
            reportId +
            '?includes=reportTemplateAliases,reportTemplateCategories,reportTemplateCategories.category,reportTagCollections,reportTagCollections.reportTag',
        )
        .then(({ data }) => data.data || null)
    } catch (error) {
      return null
    }
  }

  const mergeReportTemplateData = (obj, reportTemplateData) => {
    const reportTemplateAliases =
      reportTemplateData?.reportTemplateAliases || getLoadedReportAliases(obj)
    const reportTemplateCategories =
      reportTemplateData?.reportTemplateCategories ||
      obj?.reportTemplate?.reportTemplateCategories ||
      obj?.reportTemplateCategories ||
      []
    const reportTagCollections =
      reportTemplateData?.reportTagCollections ||
      obj?.reportTemplate?.reportTagCollections ||
      obj?.reportTagCollections ||
      []

    return {
      ...obj,
      reportTemplateAliases,
      reportTemplateCategories,
      reportTagCollections,
      reportTemplate: {
        ...(obj?.reportTemplate || {}),
        ...(reportTemplateData || {}),
        reportTemplateAliases,
        reportTemplateCategories,
        reportTagCollections,
      },
    }
  }

  const mergeReportTemplateAliases = (obj, aliases) =>
    mergeReportTemplateData(obj, { reportTemplateAliases: aliases })

  const stripReportTemplateRelations = (obj = {}) => {
    const {
      reportTemplate,
      reportTemplateAliases,
      reportTemplateCategories,
      reportTagCollections,
      reporttemplatealias,
      ...webviewData
    } = obj || {}

    return webviewData
  }

  useEffect(async () => {
    const query = new URLSearchParams(useSearch)
    //const id = query.get('id')

    console.log('createreport.id', id)
    const reportId = reportTemplateId ? reportTemplateId : id
    setIsReportDataLoaded(false)
    if (reportId) {
      setSyReportId(reportId)
      //let obj = await httpClient.get('/coresystem/syreport/'+id).then(({data})=>data.data)
      const reportUrl = revisionId
        ? `/report/report-webview/revisions/${revisionId}?reportTemplateId=${reportId}`
        : `/report/report-webview?reportTemplateId=${reportId}&include=reportTemplate,reportTemplateAliases`
      let obj = await httpClient
        .get(reportUrl)
        .then(({ data }) => data.data)
      if(obj){
        const reportTemplateData = await loadReportTemplateData(reportId)
        if (reportTemplateData) {
          obj = mergeReportTemplateData(obj, reportTemplateData)
        }
        if (revisionId) {
          setActiveRevision(obj)
        }
        applyReportData(obj)
        setIsReportDataLoaded(true)
      }else{
        Swal.fire({
          title: "ไม่มี Permission",
          icon: "error",
          text: `Permission Deny for this report
        Please contact Admin to grant this.!`,
          confirmButtonText: "OK, รับทราบ",
          allowOutsideClick: false,   // ❌ กันคลิกนอก
          allowEscapeKey: false,      // ❌ กันกด ESC
          allowEnterKey: true,        // ✔️ ให้กด Enter = OK
        }).then((result) => {
          if (result.isConfirmed) {
            //window.location.href = rootPath + '/hospital/hosp-report-classic'; // 👉 เปลี่ยน URL ที่ต้องการ
            routeHistory.push({
              pathname: `${rootPath}/hospital/hosp-report-classic`,
            });
          }
        });
      }
    } else {
      setIsReportDataLoaded(true)
    }
    await loadMetaData()
  }, [])


  const loadMetaData = async () => {
    const [mlistRes, tlistRes, tagListRes] = await Promise.allSettled([
      httpClient.get('/report/categories'),
      httpClient.get('/report/tenant-access'),
      httpClient.get('/report/report-tags'),
    ]);

    if (mlistRes.status === 'fulfilled') {
      const mlist = mlistRes.value.data.data;
      setCategoryList(
        mlist.map(item => ({
          label: item.name,
          value: item.categoryId,
          iconName: null,
        }))
      );
    }

    if (tlistRes.status === 'fulfilled') {
      const tlist = tlistRes.value.data.data;
      setTenantList(
        tlist.map(item => ({
          label: item.tenantCode + ' - ' + item.name,
          value: item.tenantCode,
          iconName: null,
        }))
      );
    }

    if (tagListRes.status === 'fulfilled') {
      const tagList = Array.isArray(tagListRes.value?.data?.data)
        ? tagListRes.value.data.data
        : []
      if (!Array.isArray(tagListRes.value?.data?.data)) {
        console.warn('report tag list response is not array', tagListRes.value?.data)
      }
      setReportTagList((prev) =>
        mergeReportTagOptions(
          tagList.map(mapReportTagOption),
          [...prev, ...reportTags],
        ),
      )
    } else {
      console.error(
        'load report tag list failed (/report/report-tags)',
        tagListRes.reason?.response?.status,
        tagListRes.reason?.response?.data || tagListRes.reason,
      )
    }
  };

  const getPublishCategoryIds = (publish = {}) =>
    normalizeReportCategoryIds(
      Array.isArray(publish.categoryIds)
        ? publish.categoryIds
        : publish.categoryId
        ? [publish.categoryId]
        : [],
    )

  const getPublishDataWithCategories = (publish = {}) => {
    const categoryIds = getPublishCategoryIds(publish)
    const tagIds = normalizeReportTagIds(publish.tagIds || [])
    const primaryCategory = categoryList.find(
      (item) => String(item.value) === String(categoryIds[0]),
    )

    return {
      ...publish,
      categoryIds,
      categoryId: categoryIds[0] || null,
      tagIds,
      moduleName: primaryCategory?.label || publish.moduleName || moduleName || '',
    }
  }

  const syncReportTemplateCategories = async (reportTemplateId, categoryIds = []) => {
    if (!reportTemplateId) return

    const nextCategoryIds = normalizeReportCategoryIds(categoryIds)
    const currentCategories = await httpClient
      .get(
        '/report/report-templates/' +
          reportTemplateId +
          '?includes=reportTemplateCategories,reportTemplateCategories.category',
      )
      .then(({ data }) => data.data?.reportTemplateCategories || [])
      .catch(() => [])
    const currentCategoryIds = normalizeReportCategoryIds(
      currentCategories.map(getReportCategoryIdFromItem),
    )
    const addCategoryIds = nextCategoryIds.filter(
      (categoryId) => !currentCategoryIds.includes(categoryId),
    )
    const deleteCategoryIds = currentCategoryIds.filter(
      (categoryId) => !nextCategoryIds.includes(categoryId),
    )

    await Promise.all(
      addCategoryIds.map((categoryId) =>
        httpClient.post(
          '/report/report-templates/' + reportTemplateId + '/categories/' + categoryId,
        ),
      ),
    )
    await Promise.all(
      deleteCategoryIds.map((categoryId) =>
        httpClient.delete(
          '/report/report-templates/' + reportTemplateId + '/categories/' + categoryId,
        ),
      ),
    )
  }

  const getSelectedReportTagsFromPublish = (publish = {}, options = reportTagList) => {
    const tagIds = normalizeReportTagIds(publish.tagIds || [])
    const selectedExistingTags = tagIds
      .map((tagId) => options.find((item) => String(item.value) === String(tagId)))
      .filter((tag) => tag)

    return mergeReportTagOptions(selectedExistingTags, [])
  }

  const syncReportTemplateTags = async (reportTemplateId, tagIds = []) => {
    if (!reportTemplateId) return

    const nextTagIds = normalizeReportTagIds(tagIds)
    const currentTagCollections = await httpClient
      .get(
        '/report/report-templates/' +
          reportTemplateId +
          '?includes=reportTagCollections,reportTagCollections.reportTag',
      )
      .then(({ data }) => data.data?.reportTagCollections || [])
      .catch(() => [])
    const currentTagIds = normalizeReportTagIds(
      currentTagCollections.map(getReportTagIdFromItem),
    )
    const addTagIds = nextTagIds.filter(
      (reportTagId) => !currentTagIds.includes(reportTagId),
    )
    const deleteTagIds = currentTagIds.filter(
      (reportTagId) => !nextTagIds.includes(reportTagId),
    )

    await Promise.all(
      addTagIds.map((reportTagId) =>
        httpClient.post(
          '/report/report-templates/' + reportTemplateId + '/tags/' + reportTagId,
        ),
      ),
    )
    await Promise.all(
      deleteTagIds.map((reportTagId) =>
        httpClient.delete(
          '/report/report-templates/' + reportTemplateId + '/tags/' + reportTagId,
        ),
      ),
    )
  }

  const applyReportData = (obj) => {
    // const permissionCheck = obj?.publishData?.permissions || [];
    // const userGroup = user?.groups[0];
    // if (permissionCheck?.length > 0) {
    //   let groups = permissionCheck?.map((item) => item.name).join(", ");
    //   setPermissionsDefault(permissionCheck);
    //   const hasPermission = permissionCheck?.some(
    //     (perm) => perm.path === userGroup
    //   );
    //   if (!hasPermission) {
    //     Swal.fire({
    //       title: "ไม่มี Permisssion",
    //       icon: "error",
    //       text: `Permission Deny for Group [${groups}]
    //         Please contact Admin to grant this.! `,
    //       confirmButtonText: "OK",
    //     }).then(() => {
    //       history.push({
    //         pathname: `/admin-panel/hospital/hosp-report-classic`,
    //       });
    //     });
    //   }
    // }
    const relationCategoryIds = getReportTemplateCategoryIds(obj)
    const publishCategoryIds = normalizeReportCategoryIds(
      obj?.publishData?.categoryIds ||
        (obj?.publishData?.categoryId ? [obj.publishData.categoryId] : []),
    )
    const categoryIds = relationCategoryIds.length
      ? relationCategoryIds
      : publishCategoryIds
    const primaryCategoryName = getPrimaryReportCategoryName(obj)
    obj.publishData = obj.publishData || {}
    const relationTagIds = getReportTemplateTagIds(obj)
    const publishTagIds = normalizeReportTagIds(obj?.publishData?.tagIds || [])
    const tagIds = relationTagIds.length ? relationTagIds : publishTagIds
    obj.publishData.categoryIds = categoryIds
    obj.publishData.categoryId = categoryIds[0] || null
    obj.publishData.tagIds = tagIds
    delete obj.publishData.newTags
    obj.publishData.moduleName =
      primaryCategoryName || obj.moduleName || obj.publishData?.moduleName || ''
    obj.publishData.reportTenantCode =  obj?.reportTenantCode ||obj?.publishData?.reportTenantCode ||  ''
    obj.publishData.reportUuid =  obj?.reportUuid || obj?.publishData?.reportUuid || syReport?.publishData?.reportUuid ||''
    const dsList = obj.dataSourceData?.list ? obj.dataSourceData.list : []
    const printData = convertValue(dsList, obj.printData ? obj.printData : {})
    const newLayouts = Object.assign({}, layout, obj.layoutData)
    const paramsData = obj.paramsData?.inputs || []
    setLayout(newLayouts)
    setReportName(obj?.reportName)
    setReportCode(obj?.reportCode || '')
    setReportAliases(getLoadedReportAliases(obj))
    const loadedReportTags = hasReportTagCollectionData(obj) ? getReportTags(obj) : []
    setReportTags(loadedReportTags)
    if (loadedReportTags.length) {
      setReportTagList((prev) => mergeReportTagOptions(prev, loadedReportTags))
    }
    setModuleName(obj?.publishData?.moduleName || obj?.moduleName)
    setDataSourceList(dsList)
    setBoxes(printData)
    setReportHistory({
      past: [],
      present: cloneReportData(printData),
      future: [],
    })
    setSyReport(stripReportTemplateRelations(obj))
    //console.log('obj',obj);

    let list = []
    dsList?.forEach((element) => {
      const paramInputs = element.params?.inputs || []
      paramInputs.forEach((item) => {
        let key = item.substring(2, item.length - 2)
        //find in paramsData
        let find = paramsData.find(({ title }) => title === key) || {}
        list.push({ ...find, title: key, value: '', field: key })
      })
    })
    list = _.uniqBy(list, 'field')

    setInputs(list)

    var keys = Object.keys(obj.printData)
    var last = keys.length >= 0 ? keys[keys.length - 1] : null
    //console.log('k',keys,last)
    if (last) setMaxId(last * 1)
  }

  const onAiApply = (jsonData) => {
    const obj = {
      moduleName: moduleName,
      reportName: reportName,
      reportCode: reportCode,
    }
    applyReportData({ ...jsonData, ...obj })
  }

  useEffect(async () => {
    const query = new URLSearchParams(useSearch)
    const id = query.get('id')
    if (id) {
      //let obj = await httpClient.get('/coresystem/syreport/find?convertQuery=true&syReportId='+id).then(({data})=>data.data)
      //console.log("obj",obj)
    }
  }, [])

  useEffect(async () => {
    //await onMenuOpen()
  }, [syReport.publishData.moduleName])

  const isTypingTarget = (target) =>
    target?.closest &&
    target.closest('input, textarea, select, [contenteditable="true"]')

  const getPositionValue = (value) => {
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const pdfOverlayMode = isPdfOverlayOutputMode(syReport.publishData)
  const pdfTemplateMetadataPending =
    pdfOverlayMode && isPdfTemplateMetadataPending(boxes, layout)
  const invalidatePdfGeometry = useCallback((item) => {
    if (!pdfOverlayMode) return item

    return invalidateChangedPdfGeometry(item, boxes[item.id])
  }, [pdfOverlayMode, boxes])

  const getCurrentPageItems = () =>
    Object.values(boxes)
      .filter((item) => layout.pages <= 1 || (item.page ?? 1) === currPage)
      .sort((a, b) => {
        const topDiff = getPositionValue(a.top) - getPositionValue(b.top)
        if (topDiff !== 0) return topDiff

        const leftDiff = getPositionValue(a.left) - getPositionValue(b.left)
        if (leftDiff !== 0) return leftDiff

        return String(a.id).localeCompare(String(b.id))
      })

  const selectReportItem = (item) => {
    if (!item?.id) return

    setActiveItem({ ...item, inputFocus: false })
    setSelectedItemIds([String(item.id)])
    setOpenId(PARAMETER_INDEX)
  }

  const selectAdjacentReportItem = (isPrevious = false) => {
    const reportItems = getCurrentPageItems()
    if (!reportItems.length) return

    const activeIndex = reportItems.findIndex(
      (item) => String(item.id) === String(activeItem.id),
    )
    if (activeIndex < 0) {
      selectReportItem(
        isPrevious ? reportItems[reportItems.length - 1] : reportItems[0],
      )
      return
    }

    const nextIndex = isPrevious
      ? (activeIndex - 1 + reportItems.length) % reportItems.length
      : (activeIndex + 1) % reportItems.length

    selectReportItem(reportItems[nextIndex])
  }

  const getMoveItemIds = () => {
    const activeId = String(activeItem.id)
    const existingSelectedIds = selectedItemIds.filter(
      (id) =>
        boxes[id] && (layout.pages <= 1 || (boxes[id].page ?? 1) === currPage),
    )

    if (
      existingSelectedIds.length > 1 &&
      existingSelectedIds.includes(activeId)
    ) {
      return existingSelectedIds
    }

    return activeItem.id && boxes[activeId] ? [activeId] : []
  }

  const moveSelectedReportItems = (moveOffset) => {
    const moveItemIds = getMoveItemIds()
    if (!moveItemIds.length) return

    const newBoxes = cloneReportData(boxes)

    moveItemIds.forEach((id) => {
      newBoxes[id] = invalidatePdfGeometry({
        ...newBoxes[id],
        top: getPositionValue(newBoxes[id].top) + moveOffset.top,
        left: getPositionValue(newBoxes[id].left) + moveOffset.left,
      })
    })

    const nextActiveItem =
      newBoxes[String(activeItem.id)] || newBoxes[moveItemIds[0]]
    setActiveItem(nextActiveItem)
    setSelectedItemIds(moveItemIds)
    updateBoxes(newBoxes)
  }

  const removeSelectedReportItems = () => {
    const removeItemIds = getMoveItemIds()
    if (!removeItemIds.length) return

    const newBoxes = cloneReportData(boxes)
    removeItemIds.forEach((id) => {
      delete newBoxes[id]
    })

    setActiveItem({})
    setSelectedItemIds([])
    updateBoxes(newBoxes)
  }

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Tab') {
        if (
          event.ctrlKey ||
          event.metaKey ||
          event.altKey ||
          isTypingTarget(event.target) ||
          activeItem?.inputFocus
        ) {
          return
        }

        event.preventDefault()
        selectAdjacentReportItem(event.shiftKey)
        return
      }

      if (event.key === 'Delete') {
        if (
          event.ctrlKey ||
          event.metaKey ||
          event.altKey ||
          isTypingTarget(event.target) ||
          activeItem?.inputFocus ||
          !activeItem?.id
        ) {
          return
        }

        event.preventDefault()
        removeSelectedReportItems()
        return
      }

      if (
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
      ) {
        const OFFSET = 5
        const moveOffset = {
          ArrowUp: { top: -OFFSET, left: 0 },
          ArrowDown: { top: OFFSET, left: 0 },
          ArrowLeft: { top: 0, left: -OFFSET },
          ArrowRight: { top: 0, left: OFFSET },
        }[event.key]

        if (
          event.ctrlKey ||
          event.metaKey ||
          event.altKey ||
          isTypingTarget(event.target) ||
          activeItem?.inputFocus
        ) {
          return
        }

        event.preventDefault()
        console.log('key', activeItem, event)
        if (
          activeItem &&
          activeItem.id &&
          (activeItem.inputFocus === undefined ||
            activeItem?.inputFocus === false)
        ) {
          moveSelectedReportItems(moveOffset)
        } else {
          window.removeEventListener('keydown', handleKeyDown)
        }
      } else if (event.key === 'Escape') {
        // Your Escape key logic here
        console.log('Escape key pressed')
        if (activeItem && activeItem.id) {
          setActiveItem({})
        }
      }
    }

    if (activeItem && activeItem.id)
      window.addEventListener('keydown', handleKeyDown)
    else window.removeEventListener('keydown', handleKeyDown)

    return () => {
      // Clean up the event listener when the component unmounts
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [activeItem, boxes, currPage, layout.pages, selectedItemIds])

  const onMenuOpen = async () => {
    if (syReport.publishData.moduleName != '') {
      const list = await httpClient
        .get(
          '/coresystem/syreport/locationcode/find?moduleName=' +
            syReport.publishData.moduleName,
        )
        .then(({ data }) => data.data)
      setLocationList(
        list.map((item, index) => {
          return {
            label: item.locationName,
            value: item.locationCode,
            item: item,
          }
        }),
      )
    }
  }

  const convertValue = (dsList, data) => {
    let dsData = createReportDataSourceMap(dsList)

    Object.keys(data).map((key, index) => {
      const bindValue = getReportBindValue(dsData, data[key].name)
      if (
        data[key].type === 'image' &&
        typeof bindValue !== 'undefined'
      ) {
        data[key].value = bindValue
      }

      if (data[key].type == 'choice') {
        if (dsData._dsName[data[key].name])
          data[key].value = dsData._dsName[data[key].name]
      }
    })
    return data
  }

  const applyImageBindValue = (item, sourceList = dataSourceList) => {
    if (item?.type !== 'image') return item

    const bindValue = getReportBindValue(
      createReportDataSourceMap(sourceList),
      item?.name,
    )
    if (typeof bindValue === 'undefined') return item

    return { ...item, value: bindValue }
  }

  const currentHash = window.location.hash
  // ผลลัพธ์: '#/super-admin/admin-panel/hospital/hosp-report-classic'

  // ตัด # แล้ว split
  const hashPath = currentHash.replace(/^#/, '').split('/')
  const rootPath = '/' + (hashPath[1] || '')
  const path = [
    { title: 'Home', url: '/' },
    { title: 'หน้า Report', url: rootPath + '/hospital/hosp-report-classic' },
    { title: 'แก้ไข Report Designer' },
  ]

  const getCurrentReportTemplateId = () => syReportId || reportTemplateId || id

  const formatRevisionDate = (value) => {
    if (!value) return '-'

    return new Date(value).toLocaleString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getRevisionUpdatedByName = (item = {}) => {
    if (item.updatedByName) return item.updatedByName

    const updatedBy = item.updatedBy || item.revisionUpdatedBy
    const name = [updatedBy?.firstName, updatedBy?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim()

    return name || updatedBy?.code || updatedBy?.email || item.updatedById || '-'
  }

  const getClassicReportBasePath = () => {
    const hashPathname = window.location.hash.replace(/^#/, '').split('?')[0]
    const basePath = hashPathname.split('/hosp-report-classic')[0]
    return basePath + '/hosp-report-classic'
  }

  const getDesignerRevisionUrl = (historyId) =>
    `${window.location.origin}/#${getClassicReportBasePath()}/${getCurrentReportTemplateId()}/revision/${historyId}`

  const getPrintRevisionUrl = (historyId) =>
    `${window.location.origin}/#/print/report?reportTemplateId=${getCurrentReportTemplateId()}&revisionId=${historyId}`

  const openRevisionUrl = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const loadRevisionList = async (page = 1) => {
    const currentReportTemplateId = getCurrentReportTemplateId()
    if (!currentReportTemplateId) return

    setIsRevisionLoading(true)
    try {
      const { data } = await httpClient.get(
        '/report/report-webview/revisions?reportTemplateId=' +
          currentReportTemplateId +
          '&page=' +
          page +
          '&perPage=' +
          REVISION_PAGE_SIZE,
      )
      const list = Array.isArray(data?.data) ? data.data : []
      const pagination = data?.pagination || {}
      const total = Number(pagination.total ?? list.length)
      const perPage = Number(pagination.perPage || REVISION_PAGE_SIZE)
      const lastPage = Math.max(1, Number(pagination.lastPage) || Math.ceil(total / perPage) || 1)

      setRevisionList(list)
      setRevisionPagination({
        page: Number(pagination.page || page),
        perPage,
        total,
        lastPage,
      })
    } catch (err) {
      setRevisionList([])
      setRevisionPagination({
        page: 1,
        perPage: REVISION_PAGE_SIZE,
        total: 0,
        lastPage: 1,
      })
      Swal.fire({
        icon: 'error',
        title: 'โหลด revision ไม่สำเร็จ',
        text: 'ไม่สามารถดึงรายการ revision ได้',
      })
    } finally {
      setIsRevisionLoading(false)
    }
  }

  const openRevisionModal = () => {
    const currentReportTemplateId = getCurrentReportTemplateId()
    if (!currentReportTemplateId) return

    setShowRevisionModal(true)
    setRevisionList([])
    setRevisionPagination({
      page: 1,
      perPage: REVISION_PAGE_SIZE,
      total: 0,
      lastPage: 1,
    })
    loadRevisionList(1)
  }

  const handleChangeRevisionPage = (event, page) => {
    loadRevisionList(page)
  }

  const onItemClick = (item, event) => {
    const nextItem = { ...item, inputFocus: false }
    delete nextItem['showTinyModal']

    if (event?.shiftKey) {
      const itemId = String(item.id)
      setSelectedItemIds((prev) => {
        if (prev.includes(itemId)) {
          return prev.filter((id) => id !== itemId)
        }

        return [...prev, itemId]
      })
    } else {
      setSelectedItemIds([String(item.id)])
    }

    setActiveItem(nextItem)
    setOpenId(PARAMETER_INDEX)
  }

  const onItemDoubleClick = (item) => {
    if (item?.type === 'report_resource') return

    console.log('onItemDoubleClick', item)
    item.inputFocus = false
    setActiveItem({ ...item, showTinyModal: true })
    setOpenId(PARAMETER_INDEX)
  }

  const onReportAreaClick = (event) => {
    if (suppressReportAreaClickRef.current) {
      suppressReportAreaClickRef.current = false
      event.stopPropagation()
      return
    }

    if (event.target?.closest && event.target.closest('.box')) return

    clearActiveSelection()
  }

  const onAreaSelect = (itemIds = []) => {
    const selectedIds = itemIds.filter((itemId) => boxes[itemId])

    if (!selectedIds.length) {
      clearActiveSelection()
      return
    }

    const nextActiveItem = boxes[selectedIds[selectedIds.length - 1]]

    setSelectedItemIds(selectedIds)
    setActiveItem({ ...nextActiveItem, inputFocus: false })
    setOpenId(PARAMETER_INDEX)
  }

  const getReportAreaRelativePoint = (event) => {
    const node = reportAreaRef.current
    if (!node) return { x: 0, y: 0 }

    const rect = node.getBoundingClientRect()

    return {
      x: Math.max(0, Math.min(event.clientX - rect.left, rect.width)),
      y: Math.max(0, Math.min(event.clientY - rect.top, rect.height)),
    }
  }

  const getNormalizedAreaSelectionBox = (box) => {
    if (!box) return null

    return {
      left: Math.min(box.startX, box.endX),
      top: Math.min(box.startY, box.endY),
      width: Math.abs(box.endX - box.startX),
      height: Math.abs(box.endY - box.startY),
    }
  }

  const boxesIntersect = (a, b) =>
    a.left < b.left + b.width &&
    a.left + a.width > b.left &&
    a.top < b.top + b.height &&
    a.top + a.height > b.top

  const selectReportBoxesInArea = (box) => {
    const node = reportAreaRef.current
    const selection = getNormalizedAreaSelectionBox(box)
    if (!node || !selection) return

    if (selection.width < 4 && selection.height < 4) return

    const nodeRect = node.getBoundingClientRect()
    const selectedIds = Array.from(node.querySelectorAll('[data-box-id]'))
      .filter((boxNode) => {
        const boxRect = boxNode.getBoundingClientRect()

        return boxesIntersect(selection, {
          left: boxRect.left - nodeRect.left,
          top: boxRect.top - nodeRect.top,
          width: boxRect.width,
          height: boxRect.height,
        })
      })
      .map((boxNode) => boxNode.dataset.boxId)

    onAreaSelect(selectedIds)
    suppressReportAreaClickRef.current = true
  }

  const onReportAreaMouseDown = (event) => {
    if (
      event.button !== 0 ||
      event.target?.closest?.('.box') ||
      event.target?.closest?.('.print-area') ||
      event.target?.closest?.(
        'input, textarea, select, button, [contenteditable="true"]',
      )
    ) {
      return
    }

    const point = getReportAreaRelativePoint(event)
    const nextSelectionBox = {
      startX: point.x,
      startY: point.y,
      endX: point.x,
      endY: point.y,
    }

    areaSelectionBoxRef.current = nextSelectionBox
    setAreaSelectionBox(nextSelectionBox)
    event.preventDefault()
  }

  useEffect(() => {
    if (!areaSelectionBox) return undefined

    const onMouseMove = (event) => {
      const point = getReportAreaRelativePoint(event)
      const nextSelectionBox = {
        ...areaSelectionBoxRef.current,
        endX: point.x,
        endY: point.y,
      }

      areaSelectionBoxRef.current = nextSelectionBox
      setAreaSelectionBox(nextSelectionBox)
    }

    const onMouseUp = () => {
      selectReportBoxesInArea(areaSelectionBoxRef.current)
      areaSelectionBoxRef.current = null
      setAreaSelectionBox(null)
      window.setTimeout(() => {
        suppressReportAreaClickRef.current = false
      }, 0)
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [areaSelectionBox, boxes])

  const onItemAdd = useCallback(
    (item) => {
      let id = maxId + 1
      const newItem = Object.assign(
        { id: id, page: currPage },
        DEFAULT_BOX,
        cloneReportData(item),
      )

      if (newItem.type === 'advance_table') {
        newItem.fieldVariable.props = {}
        const noOfColumn = newItem.fieldVariable?.columns
          ? newItem.fieldVariable.columns.length
          : 4
        const boxWidth = Number((690 / noOfColumn).toFixed(2))

        newItem.fieldVariable.columns.forEach((citem, cindex) => {
          newItem.fieldVariable.props[citem] = {
            id: id + '_' + citem,
            name: citem,
            parentId: id,
            title: citem,
            value: citem,
            top: 0,
            left: boxWidth * cindex,
            width: boxWidth + 'px',
            height: '25px',
            type: 'text',
          }
        })
      }

      const newBoxes = {
        ...boxes,
        [id]: newItem,
      }

      updateBoxes(newBoxes)
      setActiveItem(newItem)
      setSelectedItemIds([String(id)])
      setMaxId(id)
    },
    [maxId, boxes, currPage],
  )

  useEffect(() => {
    if (!isReportDataLoaded || isRevisionMode || !pdfOverlayMode) return

    const hasPdfTemplate = Object.values(boxes).some(
      (item) => item?.type === 'pdf',
    )
    if (hasPdfTemplate) return

    onItemAdd(createPdfTemplateResourceItem(maxId + 1))
  }, [
    boxes,
    isReportDataLoaded,
    isRevisionMode,
    maxId,
    onItemAdd,
    pdfOverlayMode,
  ])

  const onAreaChange = (item) => {
    let { id, left, top } = item
    const newBoxes = cloneReportData(boxes)
    const selectedBoxIds = selectedItemIds.filter(
      (id) =>
        newBoxes[id] &&
        (layout.pages <= 1 || (newBoxes[id].page ?? 1) === currPage),
    )

    if (item.parentId) {
      const pItem = newBoxes[item.parentId].fieldVariable.props[item.name]
      const nextItem = item.moveDelta
        ? { left, top }
        : item
      newBoxes[item.parentId].fieldVariable.props[item.name] = Object.assign(
        {},
        pItem,
        nextItem,
      )
      setActiveItem(newBoxes[item.parentId])
    } else if (
      item.moveDelta &&
      selectedBoxIds.length > 1 &&
      selectedBoxIds.includes(String(id))
    ) {
      selectedBoxIds.forEach((selectedId) => {
        newBoxes[selectedId] = invalidatePdfGeometry({
          ...newBoxes[selectedId],
          top:
            selectedId === String(id)
              ? top
              : getPositionValue(newBoxes[selectedId].top) + item.moveDelta.top,
          left:
            selectedId === String(id)
              ? left
              : getPositionValue(newBoxes[selectedId].left) +
                item.moveDelta.left,
        })
      })
      setActiveItem(newBoxes[id])
      setSelectedItemIds(selectedBoxIds)
    } else {
      const { moveDelta, ...itemUpdates } = item
      const nextItemUpdates = moveDelta ? {} : itemUpdates
      newBoxes[id] = Object.assign({}, newBoxes[id], nextItemUpdates, {
        id,
        left,
        top,
      })
      newBoxes[id] = invalidatePdfGeometry(newBoxes[id])
      setActiveItem(newBoxes[item.id])
    }

    updateBoxes(newBoxes)
  }

  const onPropFocus = useCallback(
    (item) => {
      const newItem = Object.assign({}, boxes[item.id], item, {
        inputFocus: true,
      })

      setActiveItem(newItem)
    },
    [activeItem, boxes],
  )

  const onPropCopy = useCallback((item)=>{

  },[activeItem,boxes])

  const onPropChange = useCallback(
    (item) => {
      const currentItem = boxes[item.id]
      const newItem = invalidatePdfGeometry(
        Object.assign(
          {},
          currentItem,
          applyImageBindValue(item),
        ),
      )
      const currentAttachmentId =
        getAttachmentId(currentItem) || getAttachmentId(currentItem?.pdf)
      const nextAttachmentId =
        getAttachmentId(newItem) || getAttachmentId(newItem?.pdf)
      const isPdfTemplateReplacement = Boolean(
        pdfOverlayMode &&
          newItem?.type === 'pdf' &&
          nextAttachmentId &&
          currentAttachmentId !== nextAttachmentId,
      )
      let newBoxes = {
        ...boxes,
        [item.id]: newItem,
      }

      if (isPdfTemplateReplacement) {
        newBoxes = clearPdfOverlayGeometry(newBoxes)
        setLayout((currentLayout) => {
          const nextLayout = { ...currentLayout }
          delete nextLayout.pdfTemplate
          return nextLayout
        })
      }

      updateBoxes(newBoxes)
      // setBoxes(boxes)
      setActiveItem(newBoxes[item.id])
    },
    [activeItem, boxes, dataSourceList, invalidatePdfGeometry, pdfOverlayMode],
  )

  const onPropRemove = (item) => {
    const newBoxes = { ...boxes }
    delete newBoxes[item.id]
    setActiveItem({})
    setSelectedItemIds((prev) => prev.filter((id) => id !== String(item.id)))
    updateBoxes(newBoxes)
  }

  const onPropReload = useCallback(
    (item) => {
      const box = boxes[item.id]
      let findItem = dataSourceList.find((e) => e.dataSourceName === item.name)
      if (findItem && findItem.fieldVariable.value) {
        item.value = findItem.fieldVariable.value
        const newItem = Object.assign({}, boxes[item.id], item)
        const newBoxes = {
          ...boxes,
          [item.id]: newItem,
        }

        updateBoxes(newBoxes)
        // setBoxes(boxes)
        setActiveItem(newItem)
      }
    },
    [activeItem, boxes],
  )

  const getPage = (item) => item.page ?? 1

  const onLayoutChange = async (layout) => {
    //console.log('layout', layout)
    if(layout?.pages==2){
      //Ask to copy
      const result = await Swal.fire({
        title: 'Copy layout?',
        text: 'Do you want to copy data from page 1 to page 2?',
        icon: 'question',
        showCancelButton: false,
        showDenyButton: true,
        confirmButtonText: 'Copy',
        denyButtonText: "Skip, don't copy",
        cancelButtonText: 'Cancel',
        reverseButtons: true,
      })

      if (result.isConfirmed) {
        const boxesArray = Object.values(boxes)
        const page1Items = boxesArray.filter((item) => getPage(item) === 1)
        let currentId = Math.max(...Object.keys(boxes).map((k) => Number(k)), 0)

        const copiedItems = page1Items.map((item) => {
          currentId += 1
          return invalidatePdfGeometry({
            ...cloneReportData(item),
            id: String(currentId),
            page: 2,
          })
        })

        const filtered = boxesArray.filter((item) => getPage(item) !== 2)
        const newBoxes = [...filtered, ...copiedItems].reduce((acc, item) => {
          acc[item.id] = item
          return acc
        }, {})

        updateBoxes(newBoxes)
        setMaxId(currentId)
      } else if (result.isDenied) {
        // 👉 skip copy
        console.log('User chose SKIP')
      } else {
        // 👉 cancel (optional rollback)
        console.log('User cancelled')
        return
      }      

    }
    setLayout({ ...layout })
  }

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isTypingTarget(e.target)) {
        return
      }

      if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        handleUndo()
      }

      if (e.ctrlKey && e.key === 'y' && !e.shiftKey) {
        e.preventDefault()
        handleRedo()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const syncActiveItemFromBoxes = (nextBoxes) => {
    const currentActiveItem = activeItemRef.current

    if (currentActiveItem?.id) {
      setActiveItem(nextBoxes[currentActiveItem.id] || {})
    }
  }

  const updateBoxes = (newBoxes) => {
    const nextBoxes = cloneReportData(newBoxes)

    setReportHistory((prev) => {
      if (_.isEqual(prev.present, nextBoxes)) return prev

      const newPast = [...prev.past, cloneReportData(prev.present)].slice(
        -MAX_HISTORY,
      )

      return {
        past: newPast,
        present: cloneReportData(nextBoxes),
        future: [],
      }
    })

    setBoxes(nextBoxes)
  }

  const onPdfGeometryChange = (geometryById = {}) => {
    setBoxes((currentBoxes) => {
      const nextBoxes = cloneReportData(currentBoxes)
      let hasChange = false

      Object.keys(geometryById).forEach((id) => {
        if (!nextBoxes[id]) return

        const geometry = geometryById[id] || {}
        // Measurements are delayed; reject results from before an import or edit.
        if (!_.isEqual(nextBoxes[id], geometry.source)) return

        const nextItem = {
          ...nextBoxes[id],
          ...geometry.changes,
        }
        if (!_.isEqual(nextBoxes[id], nextItem)) {
          nextBoxes[id] = nextItem
          hasChange = true
        }
      })

      if (!hasChange) return currentBoxes

      setReportHistory((prev) => ({
        ...prev,
        present: cloneReportData(nextBoxes),
      }))
      syncActiveItemFromBoxes(nextBoxes)
      return nextBoxes
    })
  }

  const onPdfTemplateChange = (pdfTemplate) => {
    const revisionChanged = isPdfTemplateRevisionChanged(
      layout?.pdfTemplate,
      pdfTemplate,
    )

    setBoxes((currentBoxes) => {
      if (
        !revisionChanged &&
        !hasIncompatiblePdfOverlayGeometry(currentBoxes, pdfTemplate)
      ) {
        return currentBoxes
      }

      const nextBoxes = clearPdfOverlayGeometry(currentBoxes)
      if (nextBoxes === currentBoxes) return currentBoxes

      setReportHistory((previousHistory) => ({
        ...previousHistory,
        present: cloneReportData(nextBoxes),
      }))
      syncActiveItemFromBoxes(nextBoxes)
      return nextBoxes
    })

    const pageCount = Math.max(parseInt(pdfTemplate?.pageCount, 10) || 1, 1)
    setCurrPage((currentPage) => Math.min(currentPage, pageCount))
    setLayout((currentLayout) => {
      const nextLayout = {
        ...currentLayout,
        pages: pageCount,
        pdfOverlayGeometryVersion: 2,
        pdfTemplate,
      }

      return _.isEqual(currentLayout, nextLayout) ? currentLayout : nextLayout
    })
  }

  const handleUndo = () => {
    setReportHistory((prev) => {
      if (!prev.past.length) return prev

      const previous = prev.past[prev.past.length - 1]

      const nextBoxes = cloneReportData(previous)
      setBoxes(nextBoxes)
      syncActiveItemFromBoxes(nextBoxes)

      return {
        past: prev.past.slice(0, -1),
        present: cloneReportData(previous),
        future: [cloneReportData(prev.present), ...prev.future],
      }
    })
  }

  const handleRedo = () => {
    setReportHistory((prev) => {
      if (!prev.future.length) return prev

      const next = prev.future[0]

      const nextBoxes = cloneReportData(next)
      setBoxes(nextBoxes)
      syncActiveItemFromBoxes(nextBoxes)

      return {
        past: [...prev.past, cloneReportData(prev.present)],
        present: cloneReportData(next),
        future: prev.future.slice(1),
      }
    })
  }

  const alignSelectedItems = (axis) => {
    const existingSelectedIds = selectedItemIds.filter(
      (id) =>
        boxes[id] && (layout.pages <= 1 || (boxes[id].page ?? 1) === currPage),
    )
    if (existingSelectedIds.length < 2) return

    const anchor = boxes[existingSelectedIds[0]]
    const newBoxes = cloneReportData(boxes)

    existingSelectedIds.slice(1).forEach((id) => {
      newBoxes[id] = invalidatePdfGeometry({
        ...newBoxes[id],
        [axis]: anchor[axis],
      })
    })

    updateBoxes(newBoxes)
    setActiveItem(newBoxes[existingSelectedIds[existingSelectedIds.length - 1]])
    setSelectedItemIds(existingSelectedIds)
  }

  const updateData = (field, updateValue) => {
    if (field === 'reportName') setReportName(updateValue)
    else if (field === 'reportCode') setReportCode(updateValue)
  }

  const openReportAliasModal = (index = null) => {
    const nextIndex = typeof index === 'number' ? index : null
    setAliasModalIndex(nextIndex)
    setAliasModalCode(
      nextIndex !== null ? reportAliases[nextIndex]?.code || '' : '',
    )
    setShowAliasModal(true)
  }

  const closeReportAliasModal = () => {
    setShowAliasModal(false)
    setAliasModalIndex(null)
    setAliasModalCode('')
  }

  const saveReportAlias = () => {
    const code = normalizeAliasCode(aliasModalCode)
    const currentReportCode = normalizeAliasCode(reportCode)

    if (!code) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณาระบุ Alias',
        text: 'ระบุรหัสรายงาน alias ก่อนบันทึก',
      })
      return
    }

    if (!REPORT_ALIAS_CODE_PATTERN.test(code)) {
      Swal.fire({
        icon: 'warning',
        title: 'รูปแบบ Alias ไม่ถูกต้อง',
        text: 'ใช้ได้เฉพาะ A-Z, a-z, 0-9, _ และ - ความยาว 2-50 ตัวอักษร',
      })
      return
    }

    if (currentReportCode && code === currentReportCode) {
      Swal.fire({
        icon: 'warning',
        title: 'Alias ซ้ำกับรหัสหลัก',
        text: 'รหัส alias ต้องไม่ซ้ำกับรหัสรายงานหลัก',
      })
      return
    }

    const hasDuplicate = reportAliases.some(
      (item, index) => index !== aliasModalIndex && item.code === code,
    )
    if (hasDuplicate) {
      Swal.fire({
        icon: 'warning',
        title: 'Alias ซ้ำ',
        text: 'มีรหัส alias นี้อยู่แล้ว',
      })
      return
    }

    setReportAliases((prev) => {
      const nextAlias = {
        ...(aliasModalIndex !== null ? prev[aliasModalIndex] : {}),
        code,
        enabled: true,
      }

      if (aliasModalIndex !== null) {
        return prev.map((item, index) =>
          index === aliasModalIndex ? nextAlias : item,
        )
      }

      return [...prev, nextAlias]
    })
    closeReportAliasModal()
  }

  const deleteReportAlias = () => {
    if (aliasModalIndex === null) {
      closeReportAliasModal()
      return
    }

    setReportAliases((prev) =>
      prev.filter((item, index) => index !== aliasModalIndex),
    )
    closeReportAliasModal()
  }

  const updateSelectData = (field, updateValue) => {
    const { publishData } = syReport

    //Query API already Exist

    publishData.locationCode = updateValue
    setReportName(updateValue)
    setSyReport({ ...syReport, publishData: publishData })
  }

  const onParamChange = (item) => {
    setInputs((prevInputs) =>
      prevInputs.map(
        (input) =>
          input.title === item.title
            ? item // Update the matched item
            : input, // Return other items unchanged
      ),
    )
  }

  const onSourceChangeOld = (items) => {
    setDataSourceList(items)

    let list = []
    items.forEach((element) => {
      element.params.inputs.forEach((item) => {
        let key = item.substring(2, item.length - 2)
        list.push({ title: key, value: '', field: key })
      })
    })
    list = _.uniqBy(list, 'field')
    setInputs(list)
  }

  const onSourceChange = (items, dataSourceId = null) => {
    setDataSourceList(items)

    // 1. Build new input list from items
    let newList = []
    items.forEach((element) => {
      element.params.inputs.forEach((item) => {
        const key = item.substring(2, item.length - 2)
        newList.push({ title: key, value: '', field: key })
      })
    })

    // 2. Remove duplicates by 'field'
    newList = _.uniqBy(newList, 'field')

    // 3. Merge with existing inputs (keep old value if same field)
    setInputs((prevInputs) => {
      const merged = newList.map((item) => {
        const old = prevInputs.find((p) => p.field === item.field)
        return old ? { ...item, ...old } : item // preserve old value, label, etc.
      })
      return merged
    })

    const nextBoxes = cloneReportData(boxes)
    let hasImageBindChange = false

    Object.keys(nextBoxes).forEach((id) => {
      const nextItem = applyImageBindValue(nextBoxes[id], items)
      if (!_.isEqual(nextBoxes[id], nextItem)) {
        nextBoxes[id] = nextItem
        hasImageBindChange = true
      }
    })

    if (hasImageBindChange) {
      updateBoxes(nextBoxes)
    }
  }

  const onPublishChange = (publishData) => {
    // console.log("c",publishData)
    const nextPublishData = normalizeReportResourcePublishData(
      getPublishDataWithCategories(publishData),
    )
    if (!nextPublishData.exportExcel) {
      setDesignerViewMode('report')
    }
    setSyReport({ ...syReport, publishData: nextPublishData })
    setReportTags(getSelectedReportTagsFromPublish(nextPublishData))
  }

  const onExcelProfileChange = useCallback((dataSourceId, profile) => {
    setSyReport((currentReport) => {
      const publishData = currentReport.publishData || {}
      const excelProfile = publishData.excelProfile || {}

      return {
        ...currentReport,
        publishData: {
          ...publishData,
          excelProfile: {
            ...excelProfile,
            dataSourceId,
            dataSources: {
              ...(excelProfile.dataSources || {}),
              [dataSourceId]: profile,
            },
          },
        },
      }
    })
  }, [])

  const onExcelDataSourceChange = useCallback((dataSourceId) => {
    if (!dataSourceId) return

    setSyReport((currentReport) => {
      const publishData = currentReport.publishData || {}

      return {
        ...currentReport,
        publishData: {
          ...publishData,
          excelProfile: {
            ...(publishData.excelProfile || {}),
            dataSourceId,
          },
        },
      }
    })
  }, [])

  const onExcelProfileReset = useCallback((dataSourceId) => {
    setSyReport((currentReport) => {
      const publishData = { ...(currentReport.publishData || {}) }
      const excelProfile = publishData.excelProfile || {}
      const dataSources = { ...(excelProfile.dataSources || {}) }
      const profileOptions = { ...excelProfile }
      delete profileOptions.dataSources
      delete dataSources[dataSourceId]

      if (Object.keys(dataSources).length || Object.keys(profileOptions).length) {
        publishData.excelProfile = { ...profileOptions, dataSources }
      } else {
        delete publishData.excelProfile
      }

      return { ...currentReport, publishData }
    })
  }, [])

  const onInputChange = (index, value) => {
    //console.log('onsearch',value)
    setInputs([].concat(value))
  }

  const successSave = (response) => {
    withReactContent(Swal)
      .fire({
        icon: 'success',
        title: 'บันทึกสำเร็จ',
        text: 'ทำการบันทึกสำเร็จ',
      })
      .then((result) => {
        if (result.isConfirmed) {
          // response &&
          //   window.location.replace(
          //     `#/admin-panel/hospital/hosp-report-classic/${response.data?.reportTemplateId}`
          //   )
          // window.location.reload(true)
        }
      })
  }

  const failedSave = () => {
    withReactContent(Swal).fire({
      icon: 'error',
      title: 'บันทึกล้มเหลว',
      text: 'ทำการบันทึกล้มเหลว',
    })
  }

  const uploadReportResources = async (boxes) => {
    const uploadedBoxes = cloneReportData(boxes)

    for (const key in uploadedBoxes) {
      if (uploadedBoxes[key].type === 'image') {
        uploadedBoxes[key] = await uploadReportImageResource(uploadedBoxes[key])
      } else if (uploadedBoxes[key].type === 'pdf') {
        const file = uploadedBoxes[key].data
        if (file) {
          const attachment = await uploadReportStaticFile(file, 'report.pdf')
          uploadedBoxes[key] = buildPdfResourceItem(
            uploadedBoxes[key],
            attachment,
            file.name,
          )
        }
      }
    }

    updateBoxes(uploadedBoxes)
    return uploadedBoxes
  }

  const findSearchData = (list) => {
    let inputs = []
    ///console.log("find",list)
    list.forEach((item) => {
      if (item.params && item.params.inputs) {
        inputs = inputs.concat(item.params.inputs)
      }
    })
    const uniqueInputs = [...new Set(inputs)]
    return { inputs: uniqueInputs }
  }

  const isPatientIdentifierParam = (searchData) => {
    let isPatientReport = 0
    if (searchData.inputs) {
      searchData.inputs.forEach((param) => {
        if (['{{hn}}', '{{an}}', '{{vn}}'].includes(param)) {
          isPatientReport = 1
        }
      })
    }
    return isPatientReport
  }

  const isPatientVisitParam = (searchData) => {
    let isPatientVisit = 0
    if (searchData.inputs) {
      searchData.inputs.forEach((param) => {
        if (['{{vn}}'].includes(param)) {
          isPatientVisit = 1
        }
      })
    }
    return isPatientVisit
  }

  const getJsonData = () => {
    const publish = normalizeReportResourcePublishData(
      getPublishDataWithCategories(syReport.publishData),
    )
    const jsonData = { templateData: {}, webviewData: {} }

    const searchData = findSearchData(dataSourceList)
    const isPatientReport = isPatientIdentifierParam(searchData)
    const isPatientVisit = isPatientVisitParam(searchData)
    jsonData['templateData'] = {
      name: reportName,
      moduleName: publish.moduleName,
      code: normalizeReportTemplateCode(reportCode),
      enabled: true,
      type: 'webview',
      isPatientReport: isPatientReport,
      isVisitReport: isPatientVisit,
      reportUuid: publish?.reportUuid || '',
      reportTenantCode: publish?.reportTenantCode || '',
      reportTemplateAliases: getReportAliasPayload(reportAliases, syReportId),
    }

    jsonData['webviewData'] = Object.assign(stripReportTemplateRelations(syReport), {
      dataSourceData: { list: dataSourceList },
      printData: boxes,
      params: {},
      publishData: { ...publish, permissions: permissionsDefault,
        reportUuid: publish?.reportUuid || '',
        reportTenantCode: publish?.reportTenantCode || ''  
      },
      isEnable: '1',
      moduleName: publish.moduleName,
      reportName: reportName,
      layoutData: layout,
      searchData: searchData,
      paramsData: { inputs: inputs },
    })

    return jsonData
  }

  const getExportJsonData = async () =>
    buildPortableReportExportData(getJsonData())

  const getCodexReportContextPayload = () => ({
    routePath: window.location.hash.replace(/^#/, '').split('?')[0],
    fullUrl: window.location.href,
    reportTemplateId: Number(getCurrentReportTemplateId() || 0),
    reportName,
    reportCode: normalizeReportTemplateCode(reportCode),
    guidePath: 'docs/specs/report/README.CLASSIC_REPORT.md',
    schemaPath: 'docs/schema/ckdoctor.schema.json',
    mode: 'classic-report-editor',
    saveBehavior:
      'Return JSON only. The frontend will apply the draft to editor state; user will review and save manually.',
    currentReport: getJsonData(),
  })

  const normalizeCodexClassicReportArtifact = (artifact = {}) => {
    const action = artifact.action || artifact.webviewData?.action || artifact.report?.action
    if (action === 'patch_classic_report') {
      throw new Error('ยังไม่รองรับ patch_classic_report ในรอบนี้ ขอให้ Codex ส่ง replace_classic_report มาก่อน')
    }

    const source = artifact.webviewData || artifact.report || artifact
    const sourceData = { ...source }
    delete sourceData.action
    delete sourceData.notes
    const templateData = artifact.templateData || {}
    const currentReport = getJsonData()
    const currentWebview = currentReport.webviewData || {}
    const currentReportTemplateId = Number(getCurrentReportTemplateId() || 0)
    const artifactReportTemplateId = Number(
      sourceData.reportTemplateId ||
        artifact.reportTemplateId ||
        templateData.reportTemplateId ||
        currentReportTemplateId,
    )

    if (
      artifactReportTemplateId &&
      currentReportTemplateId &&
      artifactReportTemplateId !== currentReportTemplateId
    ) {
      throw new Error(
        `reportTemplateId ไม่ตรงกัน: Codex=${artifactReportTemplateId}, current=${currentReportTemplateId}`,
      )
    }

    const publishData = {
      ...(currentWebview.publishData || {}),
      ...(sourceData.publishData || {}),
      reportUuid:
        sourceData.publishData?.reportUuid ||
        currentWebview.publishData?.reportUuid ||
        syReport?.publishData?.reportUuid ||
        '',
      reportTenantCode:
        sourceData.publishData?.reportTenantCode ||
        currentWebview.publishData?.reportTenantCode ||
        syReport?.publishData?.reportTenantCode ||
        syReport?.reportTenantCode ||
        '',
    }

    return {
      ...currentWebview,
      ...sourceData,
      reportTemplateId: currentReportTemplateId || artifactReportTemplateId || '',
      moduleName:
        sourceData.moduleName ||
        publishData.moduleName ||
        moduleName ||
        currentWebview.moduleName ||
        '',
      reportName: templateData.name || sourceData.reportName || reportName || '',
      reportCode:
        normalizeReportTemplateCode(templateData.code) ||
        normalizeReportTemplateCode(sourceData.reportCode) ||
        normalizeReportTemplateCode(reportCode) ||
        '',
      reportTemplateAliases:
        sourceData.reportTemplateAliases ||
        templateData.reportTemplateAliases ||
        reportAliases,
      dataSourceData:
        sourceData.dataSourceData || currentWebview.dataSourceData || { list: dataSourceList },
      printData: sourceData.printData || currentWebview.printData || boxes,
      layoutData: sourceData.layoutData || currentWebview.layoutData || layout,
      paramsData: sourceData.paramsData || currentWebview.paramsData || { inputs },
      publishData,
    }
  }

  const handleCodexReportJsonArtifact = async (artifact, meta = {}) => {
    try {
      const nextReport = normalizeCodexClassicReportArtifact(artifact)
      const confirm = await Swal.fire({
        title: 'Apply JSON จาก Codex?',
        text: 'ระบบจะนำผลลัพธ์มาใส่ในหน้า editor เท่านั้น ยังไม่บันทึกลงรายงานจริง',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Apply',
        cancelButtonText: 'ยกเลิก',
      })

      if (!confirm.isConfirmed) return

      applyReportData(nextReport)
      setActiveItem({})
      setSelectedItemIds([])
      setReportDraftStatus(`Codex draft applied${meta.jobId ? ` (${meta.jobId})` : ''}`)
      Swal.fire('Codex Agent', 'นำ JSON มาใส่หน้า editor แล้ว กรุณาตรวจสอบและกด Save เอง', 'success')
    } catch (error) {
      Swal.fire('Codex Agent', error?.message || 'ไม่สามารถ apply JSON จาก Codex ได้', 'error')
    }
  }

  const getReportDraftOwner = (override = {}) => ({
    employeeCode:
      override.employeeCode ||
      user?.employeeCode ||
      sessionStorage.getItem('sessionEmployeeCode') ||
      '',
    reportTemplateId:
      override.reportTemplateId || getCurrentReportTemplateId() || '',
    reportUuid:
      override.reportUuid || syReport?.publishData?.reportUuid || '',
    reportCode: override.reportCode || normalizeReportTemplateCode(reportCode) || '',
    tenantCode:
      override.tenantCode ||
      syReport?.publishData?.reportTenantCode ||
      syReport?.reportTenantCode ||
      'GLOBAL',
  })

  const getCurrentReportDraftData = () => getJsonData()

  const getReportDraftSnapshot = (reportData = getCurrentReportDraftData()) => {
    try {
      return JSON.stringify(reportData)
    } catch (error) {
      console.error('Cannot serialize report draft data', error)
      return ''
    }
  }

  useEffect(() => {
    if (!isReportDataLoaded) {
      return undefined
    }

    if (isRevisionMode) {
      setReportDraft(null)
      setIsReportDraftLoaded(true)
      return undefined
    }

    let isMounted = true
    const owner = getReportDraftOwner()
    reportDraftOwnerRef.current = owner
    reportDraftBaseSnapshotRef.current = getReportDraftSnapshot()
    setIsReportDraftLoaded(false)

    getReportDraft(owner).then((draft) => {
      if (!isMounted) return
      setReportDraft(draft)
      setIsReportDraftLoaded(true)
      setReportDraftStatus('')
    })

    return () => {
      isMounted = false
    }
  }, [
    isReportDataLoaded,
    isRevisionMode,
    syReportId,
    reportTemplateId,
    id,
    user?.employeeCode,
  ])

  useEffect(() => {
    if (
      !isReportDataLoaded ||
      !isReportDraftLoaded ||
      isRevisionMode ||
      !reportDraftOwnerRef.current
    ) {
      return undefined
    }

    const reportData = getCurrentReportDraftData()
    const snapshot = getReportDraftSnapshot(reportData)

    if (!reportDraftBaseSnapshotRef.current) {
      reportDraftBaseSnapshotRef.current = snapshot
      return undefined
    }

    clearTimeout(reportDraftTimerRef.current)

    if (snapshot === reportDraftBaseSnapshotRef.current) {
      setReportDraftStatus('')
      return undefined
    }

    setReportDraftStatus('saving')
    reportDraftTimerRef.current = setTimeout(async () => {
      const owner = reportDraftOwnerRef.current || getReportDraftOwner()
      const draft = await saveReportDraft(owner, getCurrentReportDraftData())
      setReportDraft(draft)
      setReportDraftStatus(draft ? 'saved' : '')
    }, 1200)

    return () => clearTimeout(reportDraftTimerRef.current)
  }, [
    isReportDataLoaded,
    isReportDraftLoaded,
    isRevisionMode,
    reportName,
    reportCode,
    reportAliases,
    moduleName,
    syReport,
    dataSourceList,
    inputs,
    layout,
    boxes,
    permissionsDefault,
  ])

  useEffect(() => () => clearTimeout(reportDraftTimerRef.current), [])

  const clearCurrentReportDraft = async (owner = reportDraftOwnerRef.current) => {
    const draftOwner = owner || getReportDraftOwner()
    clearTimeout(reportDraftTimerRef.current)
    await clearReportDraft(draftOwner)
    setReportDraft(null)
    setReportDraftStatus('')
  }

  const buildReportDataFromDraft = (draft) => {
    const draftData = getReportDraftFormData(draft)
    const templateData = draftData?.templateData || {}
    const webviewData = draftData?.webviewData || {}
    const aliases = templateData.reportTemplateAliases || []

    return mergeReportTemplateAliases(
      {
        ...webviewData,
        reportName: templateData.name || webviewData.reportName,
        reportCode: templateData.code || webviewData.reportCode || '',
        reportUuid:
          templateData.reportUuid ||
          webviewData.reportUuid ||
          webviewData.publishData?.reportUuid ||
          '',
        reportTenantCode:
          templateData.tenantCode ||
          templateData.reportTenantCode ||
          webviewData.reportTenantCode ||
          webviewData.publishData?.reportTenantCode ||
          '',
      },
      aliases,
    )
  }

  const onEditReportDraft = async () => {
    if (!reportDraft) return

    const updatedAt = reportDraft.updatedAt
      ? formatRevisionDate(reportDraft.updatedAt)
      : ''
    const result = await Swal.fire({
      title: 'กลับไปแก้รายการค้าง?',
      icon: 'warning',
      text: updatedAt ? 'Auto saved ' + updatedAt : reportDraft.summary || '',
      showCancelButton: true,
      confirmButtonText: 'กลับไปแก้',
      cancelButtonText: 'ยกเลิก',
    })

    if (result.isConfirmed) {
      applyReportData(buildReportDataFromDraft(reportDraft))
      setReportDraftStatus('')
    }
  }

  const onDeleteReportDraft = async () => {
    if (!reportDraft) return

    const result = await Swal.fire({
      title: 'ลบรายการค้าง?',
      icon: 'warning',
      text: 'Report ที่ยังไม่ได้บันทึกจะถูกลบออกจากเครื่องนี้',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก',
    })

    if (result.isConfirmed) {
      await clearCurrentReportDraft(reportDraft.owner)
    }
  }

  const findMissingReportResourceCodes = async (reportData) => {
    const resourceCodes = getReportResourceCodes(reportData)
    const results = await Promise.all(
      resourceCodes.map(async (resourceCode) => {
        try {
          const response = await httpClient.get(
            '/report/report-webview/find?reportCode=' +
              encodeURIComponent(resourceCode) +
              '&include=reportTemplate',
          )
          const reportData = response?.data?.data
          return reportData?.reportTemplateId &&
            isReportResourceOutput(reportData)
            ? null
            : resourceCode
        } catch (error) {
          return resourceCode
        }
      }),
    )

    return results.filter(Boolean)
  }

  const onImportApply = async (jsonData) => {
    const resourceItems = getReportResourceItems(jsonData)
    const hasResourceWithoutCode = resourceItems.some(
      (item) => !getReportResourceCode(item),
    )
    const missingResourceCodes = await findMissingReportResourceCodes(jsonData)

    if (hasResourceWithoutCode || missingResourceCodes.length) {
      const missingList = [
        ...(hasResourceWithoutCode ? ['Missing report code'] : []),
        ...missingResourceCodes,
      ]
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Resource Report บางรายการ import ไม่ได้',
        html:
          'ไม่พบ Resource: <strong>' +
          missingList.join(', ') +
          '</strong><br/>ต้องการ Continue หรือไม่?',
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'ยกเลิก',
      })

      if (!result.isConfirmed) return false
    }

    const uploadedJsonData = await uploadPortableReportAssets(jsonData)
    applyReportData(uploadedJsonData.webviewData || uploadedJsonData)
    return true
  }

  const hasStaticPdfReport = (printData = {}) =>
    Object.values(printData || {}).some(
      (item) =>
        item?.type === 'pdf' &&
        (getAttachmentId(item) || getAttachmentId(item?.pdf)),
    )

  const onSave = async (publish) => {
    if (pdfTemplateMetadataPending) {
      await Swal.fire({
        icon: 'info',
        title: 'กำลังเตรียม PDF ใหม่',
        text: 'กรุณารอให้ระบบอ่านจำนวนหน้าและสร้างพิกัดใหม่เสร็จก่อนบันทึก',
      })
      return false
    }

    const draftOwner = reportDraftOwnerRef.current || getReportDraftOwner()
    let publishData = normalizeReportResourcePublishData(
      getPublishDataWithCategories(publish),
    )
    let savedReportTemplateId = syReportId

    try {
      const invalidResourceItems = getReportResourceItems({ printData: boxes })
        .filter((item) => !getReportResourceCode(item))
      if (invalidResourceItems.length) {
        await Swal.fire({
          icon: 'warning',
          title: 'บันทึก Report Resource ไม่ได้',
          text: 'Report Resource ต้องมี report code',
        })
        return
      }

      const nextReportTagList = reportTagList
      publishData = {
        ...publishData,
        tagIds: normalizeReportTagIds(publishData.tagIds),
      }
      delete publishData.newTags
      const uploadedBoxes = await uploadReportResources(boxes)
      const searchData = findSearchData(dataSourceList)
      const isStaticReport = hasStaticPdfReport(uploadedBoxes) ? 1 : 0
      const obj = Object.assign(stripReportTemplateRelations(syReport), {
        dataSourceData: { list: dataSourceList },
        printData: uploadedBoxes,
        params: {},
        publishData: { ...publishData, permissions: permissionsDefault },
        isEnable: '1',
        moduleName: publishData.moduleName,
        reportName: reportName,
        reportCode: normalizeReportTemplateCode(reportCode),
        layoutData: layout,
        searchData: searchData,
        paramsData: { inputs: inputs },
      })
      const isPatientReport = isPatientIdentifierParam(searchData)
      const isPatientVisit = isPatientVisitParam(searchData)
      const templateData = {
        name: reportName,
        moduleName: publishData.moduleName,
        code: normalizeReportTemplateCode(reportCode),
        enabled: true,
        type: 'webview',
        isPatientReport: isPatientReport,
        isVisitReport: isPatientVisit,
        isStaticReport,
        reportUuid: publishData?.reportUuid || '',
        tenantCode: publishData?.reportTenantCode || '',
        reportTemplateAliases: getReportAliasPayload(reportAliases, syReportId),
      }
      let response = { data: null }
      if (syReportId > 0) {
        response = await httpClient.put(
          `/report/report-templates/${syReportId}`,
          templateData,
        )

        response = (
          await httpClient.put(
            `/report/report-template-webview/${syReportId}`,
            obj,
          )
        ).data
        console.log('re', response)
      } else {
        response = (
          await httpClient.post(`/report/report-templates`, templateData)
        ).data
        obj.reportTemplateId = response.data.reportTemplateId
        savedReportTemplateId = obj.reportTemplateId
        setSyReportId(savedReportTemplateId)
        response = (
          await httpClient.post(`/report/report-template-webview`, obj)
        ).data
      }
      await syncReportTemplateCategories(
        savedReportTemplateId,
        publishData.categoryIds,
      )
      await syncReportTemplateTags(savedReportTemplateId, publishData.tagIds)
      setReportTags(getSelectedReportTagsFromPublish(publishData, nextReportTagList))
      setSyReport((currentReport) => ({
        ...currentReport,
        publishData: {
          ...currentReport.publishData,
          ...publishData,
        },
      }))
      await clearCurrentReportDraft(draftOwner)
      reportDraftOwnerRef.current = getReportDraftOwner({
        reportTemplateId: savedReportTemplateId,
      })
      const savedDraftData = getCurrentReportDraftData()
      savedDraftData.templateData = {
        ...savedDraftData.templateData,
        reportTemplateAliases: getReportAliasPayload(
          reportAliases,
          savedReportTemplateId,
        ),
      }
      if (savedDraftData.webviewData?.publishData) {
        savedDraftData.webviewData.publishData = {
          ...savedDraftData.webviewData.publishData,
          tagIds: publishData.tagIds,
        }
      }
      reportDraftBaseSnapshotRef.current = getReportDraftSnapshot(savedDraftData)
      successSave(response)
    } catch (err) {
      failedSave()
    }
    //Get Report
  }

  const handleToolbarSave = async () => {
    if (isSaving) return

    if (isRevisionMode) {
      Swal.fire({
        icon: 'info',
        title: 'Revision read-only',
        text: 'กำลังเปิด revision เก่าอยู่ หากต้องการแก้ไขให้กลับไปเปิดรายงานปัจจุบัน',
      })
      return
    }

    setIsSaving(true)
    try {
      await onSave({ langReportCode: {}, ...syReport.publishData })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSync = (dataSource) => {
    const newBoxes = { ...boxes }

    Object.keys(newBoxes).forEach((id) => {
      const box = newBoxes[id]

      if (
        box.type === 'advance_table' &&
        box.name === dataSource?.dataSourceName
      ) {
        const newColumns = dataSource.fieldVariable?.columns || []
        const oldProps = box.fieldVariable?.props || {}

        const noOfColumn = newColumns.length
        const boxWidth = Number((690 / (noOfColumn || 1)).toFixed(2))

        const newProps = { ...oldProps }

        newColumns.forEach((col, index) => {
          if (!newProps[col]) {
            newProps[col] = {
              id: id + '_' + col,
              name: col,
              parentId: id,
              title: col,
              value: col,
              top: 0,
              left: boxWidth * index,
              width: boxWidth + 'px',
              height: '25px',
              type: 'text',
            }
          }
        })

        newBoxes[id] = {
          ...box,
          fieldVariable: {
            ...box.fieldVariable,
            columns: newColumns,
            props: newProps,
          },
        }
      }
    })

    updateBoxes(newBoxes)
  }

  const renderReportDraftButton = () => {
    if (isRevisionMode || (!reportDraft && reportDraftStatus !== 'saving')) {
      return null
    }

    const updatedAt = reportDraft?.updatedAt
      ? formatRevisionDate(reportDraft.updatedAt)
      : ''
    const title = [reportDraft?.summary, updatedAt && 'Auto saved ' + updatedAt]
      .filter(Boolean)
      .join(' / ')

    return (
      <div className="d-flex align-items-center mr-2">
        <Button
          type="button"
          size="sm"
          variant="outline-warning"
          title={title}
          disabled={!reportDraft || reportDraftStatus === 'saving'}
          onClick={onEditReportDraft}
        >
          <i className="fas fa-exclamation-circle mr-1" />
          {reportDraftStatus === 'saving' ? 'กำลังจำ' : 'มีรายการค้าง'}
        </Button>
        {reportDraft && (
          <Button
            type="button"
            size="sm"
            variant="outline-danger"
            className="ml-1"
            title="ลบรายการค้าง"
            onClick={onDeleteReportDraft}
          >
            <i className="fas fa-trash-alt" />
          </Button>
        )}
      </div>
    )
  }

  const renderInput = (title, field, value, props = {}) => {
    return (
      <FormGroup>
        <Form.Label for={field} className="text-sm-right">
          {title}
        </Form.Label>
        <Form.Control
          {...props}
          type="text"
          name={field}
          value={value}
          autoComplete="off"
          onChange={(e) => updateData(field, e.target.value)}
        />
      </FormGroup>
    )
  }

  const getReportTagBadgeStyle = (tag = {}) => {
    const color = normalizeHexColor(tag.hexColor)

    return {
      backgroundColor: color || '#e9ecef',
      border: '1px solid ' + (color || '#ced4da'),
      color: color ? getContrastTextColor(color) : '#495057',
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '18px',
      maxWidth: '180px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }
  }

  const renderReportTagBadges = () => {
    if (!reportTags.length) return null

    return (
      <div className="d-flex flex-wrap justify-content-end align-items-center mr-2">
        {reportTags.map((tag) => (
          <span
            key={tag.reportTagId || tag.title}
            className="badge mr-1 mb-1"
            style={getReportTagBadgeStyle(tag)}
            title={tag.description || tag.title}
          >
            <i className="fas fa-tag mr-1" />
            {tag.title}
          </span>
        ))}
      </div>
    )
  }

  const renderReportCodeInput = () => (
    <FormGroup>
      <Form.Label for="reportCode" className="text-sm-right">
        รหัสรายงาน
      </Form.Label>
      <div className="d-flex">
        <Form.Control
          type="text"
          name="reportCode"
          value={reportCode}
          autoComplete="off"
          onChange={(e) => updateData('reportCode', e.target.value)}
        />
        <Button
          type="button"
          variant="outline-primary"
          className="ml-1"
          title="เพิ่ม alias"
          disabled={isRevisionMode}
          onClick={() => openReportAliasModal()}
        >
          <i className="fas fa-plus" />
        </Button>
      </div>
      {reportAliases.length > 0 && (
        <div className="d-flex flex-wrap mt-1">
          {reportAliases.map((alias, index) => (
            <Button
              key={alias.reportTemplateAliasId || alias.code}
              type="button"
              size="sm"
              variant="outline-secondary"
              className="mr-1 mb-1 py-0"
              title="แก้ไข alias"
              disabled={isRevisionMode}
              onClick={() => openReportAliasModal(index)}
            >
              <span>{alias.code}</span>
              <i className="fas fa-pencil-alt ml-1" />
            </Button>
          ))}
        </div>
      )}
    </FormGroup>
  )

  const renderSelect = (title, field, options, disabled = false) => {
    let item = options.find((e) => e.value === syReport.publishData[field])
    const state = syReport.publishData
    return (
      <FormGroup>
        <div>{title}</div>
        <div style={{ width: '100%' }}>
          <Select
            name={field}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            menuPortalTarget={document.body}
            isDisabled={disabled}
            options={options}
            // maxMenuHeight={options.length > 0 ? 48 * options.length : 48}
            value={
              state[field] &&
              Object.assign(
                {
                  label: state[field],
                  value: state[field],
                },
                item ? item : {},
              )
            }
            onChange={(e) => {
              updateSelectData(field, e.value)
            }}
            components={{ Option: IconOption, SingleValue: CustomSingleValue }}
          ></Select>
        </div>
      </FormGroup>
    )
  }

  // filter page
  Object.filter = (obj, predicate) =>
    Object.keys(obj)
      .filter((key) => predicate(obj[key]))
      .reduce((res, key) => Object.assign(res, { [key]: obj[key] }), {})

  const normalizedAreaSelectionBox =
    getNormalizedAreaSelectionBox(areaSelectionBox)
  const DesignerPrintArea = pdfOverlayMode
    ? PdfOverlayDesigner
    : PrintArea

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        {showBreadcrumb && <Breadcrumb data={path} />}
        <div
          className="classic-report-designer-layout"
          style={{
            display: 'flex',
            width: '100%',
            background: '#525659',
            minHeight: 'calc(100vh - 12rem)',
            paddingBottom: '48px',
          }}
        >
          <div
            className="classic-report-designer-content"
            style={{ flex: '1 1 auto', minWidth: 0 }}
          >
            <div
              style={{
                margin: '15px',
                padding: '10px',
                background: 'white',
                borderRadius: '5px',
              }}
            >
              {syReport.publishData.publishAt === 'print' ? (
                <Row>
                  <Col md={12}>
                    {renderSelect(
                      'ชื่อรายงาน',
                      'locationCode',
                      locationList,
                      true,
                    )}
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col md={8}>
                    {renderInput('ชื่อรายงาน ID '+(syReport?.publishData?.reportUuid||''), 'reportName', reportName)}
                    {isRevisionMode && (
                      <div className="text-warning small mt-1">
                        Revision #{activeRevision.revisionId} - view only - {formatRevisionDate(activeRevision.revisionUpdatedAt)}
                      </div>
                    )}
                  </Col>
                  <Col md={4}>
                    {renderReportCodeInput()}
                  </Col>
                </Row>
              )}
              <HeaderArea
                value={inputs}
                locationCode={syReport.publishData.locationCode}
                syReportId={syReportId}
                onChange={(index, value) => onInputChange(index, value)}
                showPreview={
                  syReport.publishData.printStyle ||
                  syReport.publishData?.pdfStickerStyle === true ||
                  pdfOverlayMode
                }
              />
              {syReport.publishData.exportExcel && (
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
                  <div
                    className="btn-group"
                    role="group"
                    aria-label="โหมดออกแบบรายงาน"
                  >
                    <Button
                      aria-pressed={designerViewMode === 'report'}
                      onClick={() => setDesignerViewMode('report')}
                      variant={
                        designerViewMode === 'report'
                          ? 'primary'
                          : 'outline-primary'
                      }
                    >
                      <i className="fas fa-file-alt mr-1" />
                      ออกแบบเอกสาร
                    </Button>
                    <Button
                      aria-pressed={designerViewMode === 'excel'}
                      onClick={() => setDesignerViewMode('excel')}
                      variant={
                        designerViewMode === 'excel'
                          ? 'primary'
                          : 'outline-primary'
                      }
                    >
                      <i className="fas fa-table mr-1" />
                      ออกแบบ Excel
                    </Button>
                  </div>
                  <small className="text-muted mt-1">
                    ใช้ datasource เดียวกัน และตั้งค่า Excel Profile เพิ่มเฉพาะที่จำเป็น
                  </small>
                </div>
              )}
              <Row className="d-flex justify-content-between">
                <Col>
                  <Button
                    title="Undo (Ctrl + Z)"
                    variant={
                      reportHistory.past.length
                        ? 'outline-primary'
                        : 'outline-secondary'
                    }
                    className="mx-1"
                    onClick={handleUndo}
                    disabled={!reportHistory.past.length}
                  >
                    <i className="fas fa-undo mx-1" />
                  </Button>

                  <Button
                    title="Redo (Ctrl + Y)"
                    variant={
                      reportHistory.future.length
                        ? 'outline-primary'
                        : 'outline-secondary'
                    }
                    onClick={handleRedo}
                    disabled={!reportHistory.future.length}
                  >
                    <i className="fas fa-redo mx-1" />
                  </Button>

                  <Button
                    title="Align horizontal line"
                    variant={
                      selectedItemIds.length > 1
                        ? 'outline-primary'
                        : 'outline-secondary'
                    }
                    className="mx-1"
                    onClick={() => alignSelectedItems('top')}
                    disabled={selectedItemIds.length < 2}
                  >
                    <i className="fas fa-arrows-alt-h mx-1" />
                  </Button>

                  <Button
                    title="Align vertical line"
                    variant={
                      selectedItemIds.length > 1
                        ? 'outline-primary'
                        : 'outline-secondary'
                    }
                    onClick={() => alignSelectedItems('left')}
                    disabled={selectedItemIds.length < 2}
                  >
                    <i className="fas fa-arrows-alt-v mx-1" />
                  </Button>

                  <Button
                    title="Revision history"
                    variant="outline-secondary"
                    className="mx-1"
                    onClick={openRevisionModal}
                  >
                    <i className="fas fa-history mx-1" />
                  </Button>
                </Col>

                <Col className="d-flex justify-content-end align-items-center flex-wrap">
                  {renderReportTagBadges()}
                  {renderReportDraftButton()}
                  {!isRevisionMode && (
                    <Button
                      className="mx-1"
                      title={
                        pdfTemplateMetadataPending
                          ? 'กำลังอ่าน PDF และสร้างพิกัดใหม่'
                          : 'Save'
                      }
                      onClick={handleToolbarSave}
                      disabled={isSaving || pdfTemplateMetadataPending}
                    >
                      <i className="fas fa-save mx-1" />
                    </Button>
                  )}
                </Col>
              </Row>
            </div>

            {designerViewMode === 'report' && layout.pages > 1 && (
              <Row>
                <Col md={12} className={classes.root}>
                  <Pagination
                    className={classes.paginate}
                    variant="outlined"
                    color="primary"
                    page={currPage}
                    count={layout.pages}
                    renderItem={(item) => <PaginationItem {...item} />}
                    onChange={handleChangePage}
                  />
                </Col>
              </Row>
            )}

            {designerViewMode === 'report' ? (
              <div
              ref={reportAreaRef}
              className="classic-report-designer-canvas font-size-sm"
              style={{ position: 'relative' }}
              onMouseDown={onReportAreaMouseDown}
              onClick={onReportAreaClick}
            >
              <DesignerPrintArea
                page={currPage}
                pdfOverlayMode={pdfOverlayMode}
                pdfTemplateBoxes={boxes}
                activeItem={activeItem}
                selectedItemIds={selectedItemIds}
                layout={layout}
                publish={syReport.publishData}
                dataSourceList={dataSourceList}
                boxes={
                  layout.pages > 1
                    ? Object.filter(boxes, (item) =>
                        isReportItemVisibleOnLayoutPage(item, currPage),
                      )
                    : boxes
                }
                onItemClick={(item, event) => onItemClick(item, event)}
                onItemDoubleClick={(item) => onItemDoubleClick(item)}
                onItemAdd={(item) => onItemAdd(item)}
                onItemChange={(item) => onAreaChange(item)}
                onAreaSelect={(itemIds) => onAreaSelect(itemIds)}
                onPdfGeometryChange={onPdfGeometryChange}
                onPdfTemplateChange={onPdfTemplateChange}
              />
              {normalizedAreaSelectionBox && (
                <div
                  style={{
                    position: 'absolute',
                    left: normalizedAreaSelectionBox.left,
                    top: normalizedAreaSelectionBox.top,
                    width: normalizedAreaSelectionBox.width,
                    height: normalizedAreaSelectionBox.height,
                    border: '1px dashed gray',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    pointerEvents: 'none',
                    zIndex: 9999,
                  }}
                />
              )}
              </div>
            ) : (
              <div style={{ margin: '15px' }}>
                <ExcelDataPreview
                  report={{
                    ...syReport,
                    reportName,
                    layoutData: layout,
                    reportTemplateId: syReportId,
                    dataSourceData: { list: dataSourceList },
                  }}
                  onDataSourceChange={
                    isRevisionMode ? undefined : onExcelDataSourceChange
                  }
                  onProfileChange={
                    isRevisionMode ? undefined : onExcelProfileChange
                  }
                  onProfileReset={
                    isRevisionMode ? undefined : onExcelProfileReset
                  }
                />
              </div>
            )}
          </div>
          <div
            className="report-menu classic-report-designer-sidebar"
            style={{ width: 280 }}
          >
            <AccordionChildren
              maxHeight={720}
              collapseID={openId}
              onOpenIdChange={(openId) => setOpenId(openId)}
            >
              <ResourceMenu
                title={'Resources'}
                reportId={syReportId}
                onAiApply={onAiApply}
              />
              <DataSourceMenu
                title={'Data Source'}
                categoryList={categoryList}
                variables={dataSourceList}
                onChange={(items, dataSourceId) =>
                  onSourceChange(items, dataSourceId)
                }
                handleSync={handleSync}
              />
              <ParameterMenu
                title={'Parameter'}
                inputs={inputs}
                datasource={dataSourceList}
                onChange={(item) => onParamChange(item)}
              />
              <PropertiesMenu
                title={'Properties'}
                item={activeItem}
                layout={layout}
                pdfOverlayMode={pdfOverlayMode}
                dataSourceList={dataSourceList}
                onChange={(item) => onPropChange(item)}
                onReload={(item) => onPropReload(item)}
                onRemove={(item) => onPropRemove(item)}
                onFocus={(item) => onPropFocus(item)}
                onCopy={(item)=>onPropCopy(item)}
              />
              <LayoutMenu
                title={'Layout'}
                layout={layout}
                onChange={(layout) => onLayoutChange(layout)}
              />
              {isRevisionMode ? [] : [
                <PublishMenu
                  key="publish"
                  title={'Publish'}
                  alwaysOpen={true}
                  publish={{ langReportCode: {}, ...syReport.publishData }}
                  categoryList={categoryList}
                  reportTagList={reportTagList}
                  tenantList={tenantList}
                  languageOption={languageOption}
                  onChange={(e) => onPublishChange(e)}
                  onSave={(e) => onSave(e)}
                  permissionsDefault={permissionsDefault}
                  savePermission={savePermission}
                  handleShowPermission={handleShowPermission}
                  handleClosePermission={handleClosePermission}
                  showModalPermission={showModalPermission}
                />,
                <ExportImportMenu
                  key="export-import"
                  title={'Export/Import'}
                  alwaysOpen={true}
                  publish={syReport.publishData}
                  onApply={(e) => onImportApply(e)}
                  getJsonData={getJsonData}
                  getExportJsonData={getExportJsonData}
                />,
              ]}
            </AccordionChildren>
          </div>
        </div>
        {isRevisionMode && (
          <div
            style={{
              position: 'fixed',
              right: 24,
              bottom: 24,
              zIndex: 1030,
            }}
          >
            <ExportImportMenu
              exportOnly
              exportButtonLabel="Export"
              exportButtonClassName="btn btn-warning shadow"
              exportButtonStyle={{ minWidth: 120 }}
              publish={syReport.publishData}
              getJsonData={getJsonData}
              getExportJsonData={getExportJsonData}
            />
          </div>
        )}
        {!isRevisionMode && isReportDataLoaded && (
          <CodexAgentFloatingMenu
            defaultProject="be"
            panelTitle="Codex Report Agent"
            promptPlaceholder="บอกสิ่งที่ต้องการปรับรายงานนี้ เช่น เพิ่ม datasource, จัด layout, แก้ token หรือสร้างตาราง"
            systemPrompt={CLASSIC_REPORT_CODEX_SYSTEM_PROMPT}
            contextPayload={getCodexReportContextPayload}
            onJsonArtifact={handleCodexReportJsonArtifact}
          />
        )}
        <Modal
          show={showAliasModal}
          onHide={closeReportAliasModal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {aliasModalIndex === null ? 'เพิ่ม Alias' : 'แก้ไข Alias'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <FormGroup className="mb-0">
              <Form.Label for="reportAliasCode" className="text-sm-right">
                รหัส Alias
              </Form.Label>
              <Form.Control
                type="text"
                name="reportAliasCode"
                value={aliasModalCode}
                autoComplete="off"
                onChange={(e) => setAliasModalCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    saveReportAlias()
                  }
                }}
              />
            </FormGroup>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <div>
              {aliasModalIndex !== null && (
                <Button
                  type="button"
                  variant="outline-danger"
                  onClick={deleteReportAlias}
                >
                  ลบ
                </Button>
              )}
            </div>
            <div>
              <Button
                type="button"
                variant="secondary"
                className="mr-1"
                onClick={closeReportAliasModal}
              >
                ยกเลิก
              </Button>
              <Button type="button" variant="primary" onClick={saveReportAlias}>
                บันทึก
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
        <Modal
          show={showRevisionModal}
          onHide={() => setShowRevisionModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Revision history</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {isRevisionLoading ? (
              <div className="py-3 text-center">Loading...</div>
            ) : revisionList.length ? (
              <>
                <Table responsive bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>Revision</th>
                      <th>Operation</th>
                      <th>Updated at</th>
                      <th>Updated by</th>
                      <th style={{ width: 160 }}>Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revisionList.map((item) => (
                      <tr key={item.historyId}>
                        <td>#{item.historyId}</td>
                        <td>{item.operation}</td>
                        <td>{formatRevisionDate(item.updatedAt)}</td>
                        <td>{getRevisionUpdatedByName(item)}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="mr-1"
                            title="Open designer revision"
                            onClick={() => openRevisionUrl(getDesignerRevisionUrl(item.historyId))}
                          >
                            <i className="fas fa-pencil-alt" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            title="Open print revision"
                            onClick={() => openRevisionUrl(getPrintRevisionUrl(item.historyId))}
                          >
                            <i className="fas fa-print" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {revisionPagination.lastPage > 1 && (
                  <div className="d-flex align-items-center justify-content-between mt-2">
                    <small className="text-muted">
                      {((revisionPagination.page - 1) * revisionPagination.perPage) + 1}
                      -
                      {Math.min(revisionPagination.page * revisionPagination.perPage, revisionPagination.total)}
                      {' / '}
                      {revisionPagination.total}
                    </small>
                    <Pagination
                      size="small"
                      variant="outlined"
                      color="primary"
                      page={revisionPagination.page}
                      count={revisionPagination.lastPage}
                      renderItem={(item) => <PaginationItem {...item} />}
                      onChange={handleChangeRevisionPage}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="py-3 text-center text-muted">ไม่มี revision</div>
            )}
          </Modal.Body>
        </Modal>
      </DndProvider>
    </>
  )
}

export default CreateReport
